import type { IEnv } from '../../../../types/types';
import type { IGenericListPropertiesMissing } from '../../../../hooks/types';
import getPresentation from './presentation';
import updateForFrequencyMax from './update-frequency-max';
import autoSortCriterias, { isSortLockedToState } from './sort-criterias';

export default function getDataPanelItems(env: IEnv) {
  const items = {
    cId: {
      ref: 'qListObjectDef.qDef.cId',
      type: 'string',
      show: false,
    },
    libraryId: {
      type: 'string',
      component: 'library-item',
      libraryItemType: 'dimension',
      ref: 'qListObjectDef.qLibraryId',
      translation: 'Common.Dimension',
      onBreak(itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
        if (env.flags?.isEnabled('QCB-35224_FILTERPANE_TITLE_IMPROVEMENT')) {
          const { qDef } = itemData.qListObjectDef;
          if (qDef.qLabelExpression && itemData.title === undefined) {
            // oxlint-disable-next-line no-param-reassign
            itemData.title = { qStringExpression: { qExpr: qDef.qLabelExpression } };
          } else if (itemData.title === undefined && qDef.title) {
            // oxlint-disable-next-line no-param-reassign
            itemData.title = qDef.title;
          }
        }
      },
      show(itemData: EngineAPI.IGenericListProperties) {
        return itemData.qListObjectDef.qLibraryId;
      },
    },
    field: {
      type: 'string',
      component: 'expression',
      expressionType: 'dimension',
      ref: 'qListObjectDef.qDef.qFieldDefs.0',
      translation: 'Common.Field',
      show(itemData: EngineAPI.IGenericListProperties) {
        return !itemData.qListObjectDef.qLibraryId;
      },
      change(itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
        const def = itemData.qListObjectDef.qDef;
        if (!def.qFieldLabels) {
          def.qFieldLabels = [];
        }
        [def.qFieldLabels[0]] = def.qFieldDefs ?? [];
        updateForFrequencyMax(itemData);
      },
    },
    title: {
      ref: 'title',
      type: 'string',
      expression: 'optional',
      translation: 'Common.Title',
      placeholder: env.flags?.isEnabled('QCB-35224_FILTERPANE_TITLE_IMPROVEMENT')
        ? // oxlint-disable-next-line typescript/no-explicit-any
          (data: any, handler: any) =>
            data?.title !== '' ? (handler?.layout.qListObject?.qDimensionInfo.qFallbackTitle ?? '') : ''
        : undefined,
      convertFunctions: env.flags?.isEnabled('QCB-35224_FILTERPANE_TITLE_IMPROVEMENT')
        ? {
            get(getFn: unknown, definition: unknown, args: unknown, data: EngineAPI.IGenericListProperties) {
              let { title } = data;
              if (title === undefined && !data?.qListObjectDef?.qLibraryId) {
                title = data.qListObjectDef?.qDef?.qLabelExpression || data.qListObjectDef?.qDef?.qFieldLabels?.[0];
              }
              return title ?? '';
            },
            set(
              value: unknown,
              setFn: (type: string, newValue: unknown) => void,
              definition: unknown,
              args: unknown,
              data: EngineAPI.IGenericListProperties,
            ) {
              setFn('string', value === '' ? undefined : value);
              if (data?.qListObjectDef?.qDef && !data?.qListObjectDef?.qLibraryId && value === '') {
                // oxlint-disable-next-line no-param-reassign
                data.qListObjectDef.qDef.qFieldLabels = [''];
                // oxlint-disable-next-line no-param-reassign
                data.qListObjectDef.qDef.qLabelExpression = '';
              }
            },
          }
        : undefined,
    },
    showTitle: {
      ref: 'showTitle',
      component: 'checkbox',
      type: 'boolean',
      translation: 'properties.filterpane.showTitle',
      defaultValue: true,
    },
    dimGroup: {
      type: 'items',
      grouped: true,
      translation: 'Common.Fields',
      items: {
        masterItemGroup: {
          ref: 'qListObjectDef.qLibraryId',
          component: 'data-master-item',
          fieldType: 'dimension',
          libraryItemType: 'dimension',
          translation: 'Common.Fields',
        },
      },
    },
    showSearch: {
      ref: 'searchEnabled',
      type: 'boolean',
      component: 'switch',
      translation: 'properties.filterpane.searchField',
      options: [
        {
          value: true,
          translation: 'properties.show',
        },
        {
          value: false,
          translation: 'properties.hide',
        },
      ],
      defaultValue: true,
    },
    wildCardSearchGroup: {
      type: 'items',
      grouped: true, // show a bottom border only if the below option (autoConfirm) is visible
      items: {
        wildCardSearch: {
          translation: 'properties.filterpane.searchMode',
          ref: 'wildCardSearch',
          component: 'dropdown',
          defaultValue: false,
          options: [
            {
              value: false,
              translation: 'properties.filterpane.simpleSearch',
            },
            {
              value: true,
              translation: 'properties.filterpane.wildCardSearch',
            },
          ],
          show(data: EngineAPI.IGenericListProperties) {
            return data.searchEnabled !== false;
          },
        },
      },
    },
    autoConfirm: {
      ref: 'autoConfirm',
      component: 'checkbox',
      translation: 'properties.filterpane.showSelectionsToolbar',
      defaultValue: false,
      inverted: true,
    },
    otherSettings: {
      translation: 'Accordion',
      component: 'expandable-items',
      type: 'items',
      items: {
        presentation: getPresentation(env),
        sorting: {
          type: 'items',
          translation: 'properties.sorting',
          items: {
            autoSort: {
              ref: 'qListObjectDef.qDef.autoSort',
              type: 'boolean',
              translation: 'properties.sorting',
              component: 'switch',
              defaultValue: true,
              options: [
                {
                  value: true,
                  translation: 'Common.Auto',
                },
                {
                  value: false,
                  translation: 'Common.Custom',
                },
              ],
              change(itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing /* , handler */) {
                if (itemData.qListObjectDef.qDef.autoSort) {
                  const dimension = itemData.qListObjectDef;
                  for (let i = 0; i < dimension.qDef.qSortCriterias.length; i++) {
                    dimension.qDef.qSortCriterias[i] = autoSortCriterias;
                  }
                }
              },
            },
            dimension: {
              component: 'sorting-dimension',
              ref: 'qListObjectDef',
              show(itemData: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing) {
                return !itemData.qListObjectDef.qDef.autoSort;
              },
              sortingItems: {
                state: true,
                expression: true,
                frequency: true,
                numeric: true,
                ascii: true,
              },
              // Custom sort stays available, but the state row is disabled
              // while showSelected depends on selected/locked values sorting to the top.
              sortingItemsDisabled: {
                state: (
                  _itemData: EngineAPI.ISortCriteria,
                  handler: { layout?: { representation?: { type?: string; showSelected?: boolean } } } | undefined,
                  args: { properties?: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing },
                ) => isSortLockedToState(args?.properties, handler?.layout),
              },
              sortingItemsDisclaimer: {
                state: {
                  component: 'text',
                  translation: 'properties.filterpane.image.sorting.disabledDisclaimer',
                  show(
                    _itemData: EngineAPI.ISortCriteria,
                    handler: { layout?: { representation?: { type?: string; showSelected?: boolean } } } | undefined,
                    args: { properties?: EngineAPI.IGenericListProperties & IGenericListPropertiesMissing },
                  ) {
                    return isSortLockedToState(args?.properties, handler?.layout);
                  },
                },
              },
            },
          },
        },
      },
    },
  };

  return items;
}
